<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex justify-space-between align-center">
      <span>関連キャスト</span>
      <v-btn
        v-if="!isEditing"
        icon
        small
        @click="startEdit"
      >
        <v-icon small>
          {{ icons.mdiPencil }}
        </v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <!-- 表示モード -->
      <template v-if="!isEditing">
        <div v-if="currentCasts.length > 0">
          <v-chip
            v-for="cast in currentCasts"
            :key="cast.id"
            small
            outlined
            class="mr-1 mb-1"
          >
            {{ cast.name }}
          </v-chip>
        </div>
        <div
          v-else
          class="text--secondary"
        >
          未設定
        </div>
      </template>

      <!-- 編集モード -->
      <template v-else>
        <v-select
          v-model="selectedCastIds"
          :items="availableCasts"
          :loading="isLoadingAvailable"
          item-text="name"
          item-value="id"
          label="キャストを選択"
          multiple
          chips
          small-chips
          deletable-chips
          dense
          outlined
          hide-details
          clearable
        />
        <div class="d-flex justify-end mt-3">
          <v-btn
            outlined
            small
            class="mr-2"
            @click="cancelEdit"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            small
            :loading="isSaving"
            @click="saveCasts"
          >
            <v-icon
              small
              left
            >
              {{ icons.mdiCheck }}
            </v-icon>
            保存
          </v-btn>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  ref, onMounted, watch, getCurrentInstance,
} from '@vue/composition-api'
import { mdiPencil, mdiCheck } from '@mdi/js'
import CustomerApi from '@/api/waiter/Customer'
import UserApi from '@/api/waiter/User'

export default {
  name: 'CustomerCastSelector',
  props: {
    customerId: {
      type: [Number, String],
      required: true,
    },
    casts: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const isEditing = ref(false)
    const isSaving = ref(false)
    const isLoadingAvailable = ref(false)
    const currentCasts = ref([])
    const availableCasts = ref([])
    const selectedCastIds = ref([])
    let availableCastsFetched = false

    const parseCasts = casts => (casts || []).map(c => ({
      id: Number(c.id),
      name: c.name || c.attributes?.name || '',
    }))

    const fetchAvailableCasts = async () => {
      if (availableCastsFetched) return
      isLoadingAvailable.value = true
      try {
        const res = await UserApi.getUsers('cast')
        if (res.data?.users?.data) {
          availableCasts.value = res.data.users.data.map(u => ({
            id: Number(u.id),
            name: u.attributes.name,
          }))
        }
        availableCastsFetched = true
      } catch (e) {
        console.error('Failed to fetch casts:', e)
      } finally {
        isLoadingAvailable.value = false
      }
    }

    // 軽量な専用エンドポイントでキャスト情報を取得
    const fetchCurrentCasts = async () => {
      try {
        const res = await CustomerApi.getCustomerCasts(props.customerId)
        if (res.data?.casts) {
          currentCasts.value = parseCasts(res.data.casts)
        }
      } catch (e) {
        currentCasts.value = parseCasts(props.casts)
      }
    }

    const initializeCasts = () => {
      if (props.casts && props.casts.length > 0) {
        currentCasts.value = parseCasts(props.casts)
      } else {
        fetchCurrentCasts()
      }
    }

    watch(() => props.casts, newCasts => {
      if (newCasts && newCasts.length > 0) {
        currentCasts.value = parseCasts(newCasts)
      }
    })

    onMounted(() => {
      initializeCasts()
    })

    const startEdit = async () => {
      selectedCastIds.value = currentCasts.value.map(c => c.id)
      isEditing.value = true
      // 編集開始時に初めて選択肢をフェッチ（遅延ロード）
      await fetchAvailableCasts()
    }

    const cancelEdit = () => {
      isEditing.value = false
    }

    const saveCasts = async () => {
      isSaving.value = true
      try {
        const res = await CustomerApi.updateCustomerCasts(props.customerId, selectedCastIds.value)
        if (res.data && res.data.casts) {
          currentCasts.value = parseCasts(res.data.casts)
        }
        emit('updated', currentCasts.value)
        isEditing.value = false
        vm.$toast.success('関連キャストを更新しました')
      } catch (e) {
        vm.$toast.error('関連キャストの更新に失敗しました')
      } finally {
        isSaving.value = false
      }
    }

    return {
      isEditing,
      isSaving,
      isLoadingAvailable,
      currentCasts,
      availableCasts,
      selectedCastIds,
      startEdit,
      cancelEdit,
      saveCasts,
      icons: {
        mdiPencil,
        mdiCheck,
      },
    }
  },
}
</script>
